/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { WorkApplyMailSignatureTemplateDto } from '../models/work-apply-mail-signature-template-dto';
import { WorkApplyMailSignatureTemplateResultDto } from '../models/work-apply-mail-signature-template-result-dto';
import { WorkMailSenderConfigDto } from '../models/work-mail-sender-config-dto';
import { WorkMailSenderProviderSettingsDto } from '../models/work-mail-sender-provider-settings-dto';
import { WorkMailSignaturePreviewDto } from '../models/work-mail-signature-preview-dto';
import { WorkMailSignatureRenderedDto } from '../models/work-mail-signature-rendered-dto';
import { WorkRenderMailSignatureTemplateDto } from '../models/work-render-mail-signature-template-dto';
import { WorkUpdateMailSettingsDto } from '../models/work-update-mail-settings-dto';

@Injectable({ providedIn: 'root' })
export class WorkMailSenderV2Service extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `globalMailSenderV2ControllerGetConfig()` */
  static readonly GlobalMailSenderV2ControllerGetConfigPath = '/v2/mail-sender/config';

  /**
   * Get mail sender config.
   *
   * Returns the active mail provider and whether the neutral sender must be enforced (SMTP / anti-spoofing).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `globalMailSenderV2ControllerGetConfig()` instead.
   *
   * This method doesn't expect any request body.
   */
  globalMailSenderV2ControllerGetConfig$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkMailSenderConfigDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkMailSenderV2Service.GlobalMailSenderV2ControllerGetConfigPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkMailSenderConfigDto>;
      })
    );
  }

  /**
   * Get mail sender config.
   *
   * Returns the active mail provider and whether the neutral sender must be enforced (SMTP / anti-spoofing).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `globalMailSenderV2ControllerGetConfig$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  globalMailSenderV2ControllerGetConfig(
    params?: {
    },
    context?: HttpContext
  ): Observable<WorkMailSenderConfigDto> {
    return this.globalMailSenderV2ControllerGetConfig$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkMailSenderConfigDto>): WorkMailSenderConfigDto => r.body)
    );
  }

  /** Path part for operation `globalMailSenderV2ControllerGetProviderSettings()` */
  static readonly GlobalMailSenderV2ControllerGetProviderSettingsPath = '/v2/mail-sender/provider-settings';

  /**
   * Get the mail provider settings stored on the server.
   *
   * Overview of the SMTP and Gmail configuration for the admin area. Secrets are only reported as set / not set.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `globalMailSenderV2ControllerGetProviderSettings()` instead.
   *
   * This method doesn't expect any request body.
   */
  globalMailSenderV2ControllerGetProviderSettings$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkMailSenderProviderSettingsDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkMailSenderV2Service.GlobalMailSenderV2ControllerGetProviderSettingsPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkMailSenderProviderSettingsDto>;
      })
    );
  }

  /**
   * Get the mail provider settings stored on the server.
   *
   * Overview of the SMTP and Gmail configuration for the admin area. Secrets are only reported as set / not set.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `globalMailSenderV2ControllerGetProviderSettings$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  globalMailSenderV2ControllerGetProviderSettings(
    params?: {
    },
    context?: HttpContext
  ): Observable<WorkMailSenderProviderSettingsDto> {
    return this.globalMailSenderV2ControllerGetProviderSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkMailSenderProviderSettingsDto>): WorkMailSenderProviderSettingsDto => r.body)
    );
  }

  /** Path part for operation `globalMailSenderV2ControllerUpdateSettings()` */
  static readonly GlobalMailSenderV2ControllerUpdateSettingsPath = '/v2/mail-sender/settings';

  /**
   * Update the tenant wide mail settings.
   *
   * Currently only the signature source (provider managed vs. maintained in the Work Office).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `globalMailSenderV2ControllerUpdateSettings()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  globalMailSenderV2ControllerUpdateSettings$Response(
    params: {
      body: WorkUpdateMailSettingsDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkMailSenderConfigDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkMailSenderV2Service.GlobalMailSenderV2ControllerUpdateSettingsPath, 'patch');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkMailSenderConfigDto>;
      })
    );
  }

  /**
   * Update the tenant wide mail settings.
   *
   * Currently only the signature source (provider managed vs. maintained in the Work Office).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `globalMailSenderV2ControllerUpdateSettings$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  globalMailSenderV2ControllerUpdateSettings(
    params: {
      body: WorkUpdateMailSettingsDto
    },
    context?: HttpContext
  ): Observable<WorkMailSenderConfigDto> {
    return this.globalMailSenderV2ControllerUpdateSettings$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkMailSenderConfigDto>): WorkMailSenderConfigDto => r.body)
    );
  }

  /** Path part for operation `globalMailSenderV2ControllerGetSignatureTemplateVariables()` */
  static readonly GlobalMailSenderV2ControllerGetSignatureTemplateVariablesPath = '/v2/mail-sender/signature/template-variables';

  /**
   * Get the available signature template variables.
   *
   * Placeholders that can be used in the signature template applied to all employees.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `globalMailSenderV2ControllerGetSignatureTemplateVariables()` instead.
   *
   * This method doesn't expect any request body.
   */
  globalMailSenderV2ControllerGetSignatureTemplateVariables$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<Array<string>>> {
    const rb = new RequestBuilder(this.rootUrl, WorkMailSenderV2Service.GlobalMailSenderV2ControllerGetSignatureTemplateVariablesPath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<string>>;
      })
    );
  }

  /**
   * Get the available signature template variables.
   *
   * Placeholders that can be used in the signature template applied to all employees.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `globalMailSenderV2ControllerGetSignatureTemplateVariables$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  globalMailSenderV2ControllerGetSignatureTemplateVariables(
    params?: {
    },
    context?: HttpContext
  ): Observable<Array<string>> {
    return this.globalMailSenderV2ControllerGetSignatureTemplateVariables$Response(params, context).pipe(
      map((r: StrictHttpResponse<Array<string>>): Array<string> => r.body)
    );
  }

  /** Path part for operation `globalMailSenderV2ControllerPreviewSignatureTemplate()` */
  static readonly GlobalMailSenderV2ControllerPreviewSignatureTemplatePath = '/v2/mail-sender/signature/template/preview';

  /**
   * Render a signature template.
   *
   * Renders the template with the master data of the requesting employee — preview before applying it to everybody.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `globalMailSenderV2ControllerPreviewSignatureTemplate()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  globalMailSenderV2ControllerPreviewSignatureTemplate$Response(
    params: {
      body: WorkRenderMailSignatureTemplateDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkMailSignatureRenderedDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkMailSenderV2Service.GlobalMailSenderV2ControllerPreviewSignatureTemplatePath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkMailSignatureRenderedDto>;
      })
    );
  }

  /**
   * Render a signature template.
   *
   * Renders the template with the master data of the requesting employee — preview before applying it to everybody.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `globalMailSenderV2ControllerPreviewSignatureTemplate$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  globalMailSenderV2ControllerPreviewSignatureTemplate(
    params: {
      body: WorkRenderMailSignatureTemplateDto
    },
    context?: HttpContext
  ): Observable<WorkMailSignatureRenderedDto> {
    return this.globalMailSenderV2ControllerPreviewSignatureTemplate$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkMailSignatureRenderedDto>): WorkMailSignatureRenderedDto => r.body)
    );
  }

  /** Path part for operation `globalMailSenderV2ControllerApplySignatureTemplateToAll()` */
  static readonly GlobalMailSenderV2ControllerApplySignatureTemplateToAllPath = '/v2/mail-sender/signature/template/apply-to-all';

  /**
   * Apply a signature template to all employees.
   *
   * Renders the template per employee (active and not left) and stores the result as their signature.
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `globalMailSenderV2ControllerApplySignatureTemplateToAll()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  globalMailSenderV2ControllerApplySignatureTemplateToAll$Response(
    params: {
      body: WorkApplyMailSignatureTemplateDto
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkApplyMailSignatureTemplateResultDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkMailSenderV2Service.GlobalMailSenderV2ControllerApplySignatureTemplateToAllPath, 'post');
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkApplyMailSignatureTemplateResultDto>;
      })
    );
  }

  /**
   * Apply a signature template to all employees.
   *
   * Renders the template per employee (active and not left) and stores the result as their signature.
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `globalMailSenderV2ControllerApplySignatureTemplateToAll$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  globalMailSenderV2ControllerApplySignatureTemplateToAll(
    params: {
      body: WorkApplyMailSignatureTemplateDto
    },
    context?: HttpContext
  ): Observable<WorkApplyMailSignatureTemplateResultDto> {
    return this.globalMailSenderV2ControllerApplySignatureTemplateToAll$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkApplyMailSignatureTemplateResultDto>): WorkApplyMailSignatureTemplateResultDto => r.body)
    );
  }

  /** Path part for operation `globalMailSenderV2ControllerGetOwnSignature()` */
  static readonly GlobalMailSenderV2ControllerGetOwnSignaturePath = '/v2/mail-sender/signature/me';

  /**
   * Get the own mail signature.
   *
   * Preview of the signature that gets appended to outgoing mails (Work-Office signatures only).
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `globalMailSenderV2ControllerGetOwnSignature()` instead.
   *
   * This method doesn't expect any request body.
   */
  globalMailSenderV2ControllerGetOwnSignature$Response(
    params?: {
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<WorkMailSignaturePreviewDto>> {
    const rb = new RequestBuilder(this.rootUrl, WorkMailSenderV2Service.GlobalMailSenderV2ControllerGetOwnSignaturePath, 'get');
    if (params) {
    }

    return this.http.request(
      rb.build({ responseType: 'json', accept: 'application/json', context })
    ).pipe(
      filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<WorkMailSignaturePreviewDto>;
      })
    );
  }

  /**
   * Get the own mail signature.
   *
   * Preview of the signature that gets appended to outgoing mails (Work-Office signatures only).
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `globalMailSenderV2ControllerGetOwnSignature$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  globalMailSenderV2ControllerGetOwnSignature(
    params?: {
    },
    context?: HttpContext
  ): Observable<WorkMailSignaturePreviewDto> {
    return this.globalMailSenderV2ControllerGetOwnSignature$Response(params, context).pipe(
      map((r: StrictHttpResponse<WorkMailSignaturePreviewDto>): WorkMailSignaturePreviewDto => r.body)
    );
  }

}
